import { useEffect, useMemo, useRef, useState } from 'react';
import { TeamOption } from '../../helpers/Goals.utils';
import {
  flattenTeamsWithMetadata,
  useTeamHierarchy,
} from '@devd-client/devd/components';
import {
  Box,
  Checkbox,
  Flex,
  Input,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  useOutsideClick,
  useTheme,
} from '@chakra-ui/react';
import { useGetTeams } from '../../apis';
import { Team } from '../../helpers/goals.types';
import { AiOutlineMinus, AiOutlinePlus } from 'react-icons/ai';

interface TeamSelectComponentProps {
  selectedTeams: string[];
  onTeamSelect: (team: TeamOption | TeamOption[]) => void;
  onTeamRemove: (value: string) => void;
}
export const TeamSelectComponent = ({
  selectedTeams,
  onTeamSelect,
  onTeamRemove,
}: TeamSelectComponentProps) => {
  const { data: teamsData, isLoading: teamsDataLoading } = useGetTeams();
  const { colors } = useTheme();
  const [teamDetails, setTeamDetails] = useState<
    Record<string, { label: string }>
  >({});

  const allTeamOptions = useMemo(() => {
    return flattenTeamsWithMetadata(teamsData?.dto || []);
  }, [teamsData?.dto]);

  const {
    expandedTeamPaths,
    toggleTeamExpand,
    visibleTeams: formattedTeamOptions,
  } = useTeamHierarchy(allTeamOptions);

  useEffect(() => {
    const newDetails: Record<string, { label: string }> = {};
    formattedTeamOptions.forEach((team) => {
      newDetails[team.value] = { label: team.label };
    });
    setTeamDetails(newDetails);
  }, [formattedTeamOptions]);

  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useOutsideClick({
    ref: dropdownRef,
    handler: () => {
      setIsDropdownOpen(false);
      setSearchQuery('');
    },
  });

  // Recursively get all nested subteams for a given team
  const getAllNestedSubTeams = (teamName: string): string[] => {
    const findTeam = (teams: Team[], name: string): Team | null => {
      for (const team of teams) {
        if (team.name === name) {
          return team;
        }
        if (team.subTeams && team.subTeams.length > 0) {
          const found = findTeam(team.subTeams, name);
          if (found) return found;
        }
      }
      return null;
    };

    const team = findTeam(teamsData?.dto || [], teamName);
    if (!team) return [];

    const allSubTeams: string[] = [];
    const collectSubTeams = (t: Team) => {
      if (t.subTeams && t.subTeams.length > 0) {
        t.subTeams.forEach((subTeam) => {
          allSubTeams.push(subTeam.name);
          collectSubTeams(subTeam);
        });
      }
    };

    collectSubTeams(team);
    return allSubTeams;
  };

  const isParentTeam = (teamName: string) => {
    const team = allTeamOptions.find((t) => t.value === teamName);
    return team?.hasNestedSubTeams ?? false;
  };

  const filteredTeams = useMemo(() => {
    if (!searchQuery.trim()) {
      return formattedTeamOptions;
    }

    const query = searchQuery.toLowerCase().trim();
    return formattedTeamOptions.filter(
      (team) =>
        team.label.toLowerCase().includes(query) ||
        team.value.toLowerCase().includes(query)
    );
  }, [formattedTeamOptions, searchQuery]);

  const handleTeamSelection = (team: TeamOption) => {
    setSearchQuery('');
    if (selectedTeams?.includes(team.value)) {
      onTeamRemove(team.value);
    } else {
      if (isParentTeam(team.value)) {
        // Recursively get all nested subteams at any depth
        const allNestedSubTeams = getAllNestedSubTeams(team.value);
        const teamsToAdd = [
          team,
          ...allNestedSubTeams
            .filter((childTeam: string) => !selectedTeams?.includes(childTeam))
            .map((childTeam: string) => {
              const childTeamOption = allTeamOptions.find(
                (t) => t.value === childTeam
              );
              return {
                value: childTeam,
                label: childTeam,
                depth: childTeamOption?.depth ?? 0,
                teamPath: childTeamOption?.teamPath ?? childTeam,
                hasNestedSubTeams: childTeamOption?.hasNestedSubTeams ?? false,
              };
            }),
        ];
        onTeamSelect(teamsToAdd);
      } else {
        onTeamSelect(team);
      }
    }
  };

  return (
    <Box flex="1" position="relative" ref={dropdownRef}>
      <Flex
        border="1px solid"
        borderColor="#E0E0E0"
        borderRadius="4px"
        p={2}
        flexWrap="wrap"
        gap={2}
        onClick={() => setIsDropdownOpen(true)}
        cursor="text"
        minH="40px"
        bg="white"
      >
        {Array.isArray(selectedTeams) &&
          selectedTeams?.map((teamId) => (
            <Tag
              key={teamId}
              size="md"
              borderRadius="full"
              variant="solid"
              bg="rgba(77, 77, 255, 0.1)"
              color="primary"
            >
              <TagLabel>{teamDetails[teamId]?.label || teamId}</TagLabel>
              <TagCloseButton
                onClick={(e) => {
                  e.stopPropagation();
                  onTeamRemove(teamId);
                }}
              />
            </Tag>
          ))}
        <Input
          variant="unstyled"
          placeholder={selectedTeams?.length === 0 ? 'Select Teams' : ''}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="sm"
          width="auto"
          flex={1}
          minW="120px"
        />
      </Flex>

      {isDropdownOpen && (
        <Box
          position="absolute"
          top="100%"
          left={0}
          w={'full'}
          mt={2}
          maxH="200px"
          overflowY="auto"
          backgroundColor="white"
          borderRadius="4px"
          boxShadow="md"
          zIndex={1000}
          border="1px solid"
          borderColor="#E0E0E0"
        >
          {filteredTeams?.length === 0 ? (
            <Text my={4} color="text.secondary" textAlign="center">
              No teams found
            </Text>
          ) : (
            <>
              {filteredTeams?.map((team) => {
                const depth = team.depth ?? 0;
                const hasNestedSubTeams = team.hasNestedSubTeams ?? false;
                const teamPath = team.teamPath ?? '';
                const isExpanded = expandedTeamPaths.has(teamPath);

                return (
                  <Flex
                    key={team.value}
                    p={3}
                    borderRadius="md"
                    transition="all 0.2s"
                    _hover={{
                      backgroundColor: 'rgba(77, 77, 255, 0.01)',
                      boxShadow: 'sm',
                    }}
                    borderBottom="1px solid"
                    borderColor="rgba(77, 77, 255, 0.1)"
                    align="center"
                    gap={3}
                    pl={depth > 0 ? 6 + depth * 16 : 3}
                    cursor="pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleTeamSelection(team);
                    }}
                  >
                    {hasNestedSubTeams && (
                      <Box
                        as="button"
                        onClick={(e: any) => {
                          e.stopPropagation();
                          e.preventDefault();
                          toggleTeamExpand(teamPath);
                        }}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        width="16px"
                        height="16px"
                        flexShrink={0}
                        _hover={{ bg: 'gray.100' }}
                        borderRadius="sm"
                      >
                        {isExpanded ? (
                          <AiOutlineMinus
                            size={12}
                            color={colors.text.secondary}
                          />
                        ) : (
                          <AiOutlinePlus
                            size={12}
                            color={colors.text.secondary}
                          />
                        )}
                      </Box>
                    )}
                    {!hasNestedSubTeams && depth > 0 && (
                      <Box width="16px" flexShrink={0} />
                    )}
                    <Checkbox
                      isChecked={selectedTeams.includes(team.value)}
                      mr={2}
                      color={'primary'}
                      onChange={() => {
                        /* check will handled by parent flex component */
                      }}
                      pointerEvents="none"
                    />
                    <Flex
                      bg="rgba(77, 77, 255, 0.1)"
                      color="#4d4dff"
                      borderRadius="full"
                      minW="36px"
                      minH="36px"
                      align="center"
                      justify="center"
                      fontWeight="bold"
                      fontSize="sm"
                    >
                      {team.label.charAt(0).toUpperCase()}
                    </Flex>
                    <Text
                      color="gray.800"
                      fontWeight="medium"
                      fontSize="sm"
                      isTruncated
                    >
                      {team.label}
                    </Text>
                  </Flex>
                );
              })}
            </>
          )}
        </Box>
      )}
    </Box>
  );
};
